import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Tabla genérica reusada por casi todas las páginas "Listado*" — reemplaza el
 * wrapper de `react-data-table-component` de V1. `columns`: `{key, label, sortable,
 * render(row), className}`. El orden real lo resuelve el servidor (`useListado`) —
 * esta tabla nunca reordena en cliente.
 *
 * BUG REAL CORREGIDO 2026-09-29 (prioridad 1 del usuario, con captura real de V1 vs
 * V2 lado a lado): esta tabla solo dibujaba la flechita ▲/▼ en la columna que YA
 * estaba activa como orden -- el resto de las columnas ordenables no tenían NINGÚN
 * indicador visual, así que parecían no ordenables aunque el clic sí funcionaba (el
 * `cursor-pointer` no basta como affordance). V1 (`components/DataTable/Header`)
 * siempre dibuja un ícono de "sort" fijo (`fa-sort`) en TODA columna ordenable, y lo
 * cambia por la flecha de dirección solo en la activa. Se replica ese mismo patrón
 * aquí con `ArrowUpDown` (lucide-react) como ícono neutro permanente.
 */
export default function DataTable({ columns, data, loading, empty = 'Sin registros', sortBy, sortOrder, onSort, onRowClick, rowKey, footer }) {
  // `rowKey` opcional: función `(row, i) => key` o nombre de campo. Por default usa
  // `row._id` (documentos reales de Mongo) -- pero algunas páginas (ej.
  // `MovimientosBancosPage.jsx`) muestran registros SINTÉTICOS sin `_id` propio (usan
  // `origenId` + `tipo`), lo que dejaba `key={undefined}` en todas las filas y
  // disparaba el warning de React "cada hijo debe tener una key única" (bug real
  // encontrado probando en el navegador 2026-09-28).
  const obtenerKey = (row, i) => {
    if (typeof rowKey === 'function') return rowKey(row, i);
    if (typeof rowKey === 'string') return row[rowKey] ?? i;
    return row._id ?? i;
  };
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 text-left font-semibold whitespace-nowrap ${c.sortable ? 'cursor-pointer select-none hover:text-slate-900' : ''} ${c.className || ''}`}
                  onClick={() => c.sortable && onSort && onSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && (
                      sortBy === c.key
                        ? (sortOrder === 'asc' ? <ArrowUp size={13} className="text-primary-600" /> : <ArrowDown size={13} className="text-primary-600" />)
                        : <ArrowUpDown size={13} className="text-slate-400" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
            {/* Fila opcional de filtros por columna (`filter`: nodo React por columna),
                igual que la primera fila del `<tbody>` de V1 en ListadoContenedores. */}
            {columns.some((c) => c.filter) && (
              <tr className="bg-white">
                {columns.map((c) => (
                  <th key={c.key} className="px-2 py-1.5 font-normal">{c.filter || null}</th>
                ))}
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && [...Array(6)].map((_, i) => (
              <tr key={i}>{columns.map((c) => <td key={c.key} className="px-4 py-3"><div className="h-4 skeleton rounded" /></td>)}</tr>
            ))}
            {!loading && data.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-16 text-center text-slate-400">{empty}</td></tr>
            )}
            {!loading && data.map((row, i) => (
              <tr
                key={obtenerKey(row, i)}
                className={`${row.is_active === 'No' ? 'bg-danger-50' : ''} ${onRowClick ? 'cursor-pointer' : ''} hover:bg-slate-50`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.wrap ? 'max-w-[14rem]' : 'whitespace-nowrap'} ${c.className || ''}`}>
                    {c.render ? c.render(row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && !loading && (
            <tfoot className="border-t border-slate-200">
              <tr>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 font-bold whitespace-nowrap ${c.className || ''}`}>{footer[c.key] ?? ''}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
